/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useQuery, useQueryClient } from 'react-query';
import { useState } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';

import { useIdentityUsePermissionsUseVerifyToken } from '~/hooks/identity';
import {
  useDeleteMutation,
  useEditMutation,
  usePageQuery,
  useCreateMutation,
  usePageShow,
} from './usePageQuery';
import { employees } from '~/routes';
import { Employee } from '~/types';
import {
  getEmployees,
  updateEmployee,
  createEmployee,
  deleteEmployee,
  getEmployeeAccessCode,
  getAllEmployees,
  getEmployeeRoles,
} from '~/api/employees';

export const useEmployeesQuery = (options?: any) => {
  return usePageQuery<Employee[]>(employees, getEmployees, options);
};

export const useEmployeesEdit = (initialValues: Employee, closeParent?: () => void) => {
  const [t] = useTranslation('employees');
  const dt = (key: string): string => t(`translation:${key}`);

  const { data: identityData, refetch: updateRoles } = useIdentityUsePermissionsUseVerifyToken({
    enabled: false,
  });
  const queryClient = useQueryClient();

  const { showClose } = usePageShow<Employee>(employees);

  return useEditMutation(employees, updateEmployee, {
    onMutate: closeParent,
    onSuccess: (values: any) => {
      void queryClient.invalidateQueries(employees);
      if (
        identityData?.emailAddress === initialValues.email &&
        !isEqual(initialValues.roles, values.data.roles)
      ) {
        void updateRoles();
        void showClose();
        void message.info(dt('rolesUpdated'));
      }
    },
  });
};

export const useEmployeesCreate = (closeParent: () => void) => {
  const queryClient = useQueryClient();

  return useCreateMutation(employees, (body: Employee) => createEmployee(body), {
    onMutate: closeParent,
    onSuccess() {
      void queryClient.invalidateQueries(employees);
    },
  });
};

export const useEmployeesDelete = (closeParent: () => void) => {
  const queryClient = useQueryClient();

  return useDeleteMutation(employees, deleteEmployee, {
    onMutate: closeParent,
    onSuccess() {
      void queryClient.invalidateQueries(employees);
    },
  });
};

export const useEmployeesAccessCode = () => {
  const [accessCode, setAccessCode] = useState<string>();
  const resetAccessCode = () => setAccessCode(undefined);

  const query = useEditMutation(employees, getEmployeeAccessCode, {
    onSuccess({ data }: any) {
      setAccessCode(data.accessCode);
    },
  });

  return { ...query, accessCode, resetAccessCode };
};

export const useAllEmployees = () => {
  return useQuery<Employee[]>(employees, () => getAllEmployees().then((res) => res.data), {
    staleTime: 60 * 1000,
  });
};

export const useGetEmployeeById = (id: string) => {
  const { data } = useAllEmployees();
  return data?.find((employee) => employee.id === id);
};

export const useEmployeesRoles = () => {
  return useQuery([employees, 'roles'], getEmployeeRoles, { staleTime: Infinity });
};
